import React, { useState } from "react";
import { BarChart3, AlertTriangle, Loader2, Wand2, CheckSquare, Square, Zap } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { db } from "@/api/base44Client";
import { PROFESSIONS, professionSubjects } from "@/lib/subjects";

const MIN = 20;
const FILL_MODEL = "gemini-3-flash-preview";

export function CoverageOverview() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const fill = async (p, s) => {
    if (busy) return;
    setBusy(`${p.id}-${s.id}`);
    try {
      const r = await db.admin.generateQuestions({ profession: p.id, subject: s.id, subject_name: s.name, count: 20, model: FILL_MODEL, with_images: true });
      toast.success(`${r.created} Fragen zu „${s.name}" (${p.short}) hinzugefügt`);
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["questions"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auffüllen fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  const fillAll = async () => {
    if (bulkRunning || selected.size === 0) return;
    setBulkRunning(true);
    let done = 0;
    for (const key of selected) {
      const [pid, sid] = key.split("||");
      const p = PROFESSIONS.find(x => x.id === pid);
      const s = professionSubjects(pid).find(x => x.id === sid);
      if (!p || !s) continue;
      setBusy(key);
      try {
        const r = await db.admin.generateQuestions({ profession: p.id, subject: s.id, subject_name: s.name, count: 20, model: FILL_MODEL, with_images: true });
        done += r.created || 0;
        toast.success(`✓ ${s.name} (${p.short}): ${r.created} Fragen`);
      } catch (e) {
        toast.error(`✗ ${s.name}: ${e?.response?.data?.detail || "Fehler"}`);
      }
    }
    setBusy(null);
    setBulkRunning(false);
    setSelected(new Set());
    qc.invalidateQueries({ queryKey: ["admin-questions"] });
    qc.invalidateQueries({ queryKey: ["questions"] });
    toast.success(`🎉 Fertig! ${done} Fragen insgesamt erstellt`);
  };

  const toggleSelect = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllLow = () => {
    const keys = new Set();
    rows.forEach(({ p, subs }) => subs.forEach(({ s, n }) => {
      if (n < MIN) keys.add(`${p.id}||${s.id}`);
    }));
    setSelected(keys);
  };

  const { data = [] } = useQuery({ queryKey: ["admin-questions", "coverage"], queryFn: db.admin.coverage });
  const count = (p, s) => data.find((r) => r.profession === p && r.subject === s)?.count || 0;
  const rows = PROFESSIONS.map((p) => ({ p, subs: professionSubjects(p.id).map((s) => ({ s, n: count(p.id, s.id) })) }));
  const missing = rows.reduce((a, r) => a + r.subs.filter((x) => x.n < MIN).length, 0);

  return (
    <section className="adm-card" data-testid="admin-coverage">
      <div className="adm-card-head">
        <h2 className="adm-card-title">
          <span className="adm-card-icon"><BarChart3 className="h-4 w-4" /></span>
          Fragen pro Fach
        </h2>
        <span className="adm-pill adm-pill-warn" data-testid="admin-coverage-missing">
          <AlertTriangle className="h-3 w-3" /> {missing} Fächer unter {MIN} Fragen
        </span>
      </div>

      <p className="adm-muted" style={{ marginTop: "-0.5rem", marginBottom: "0.9rem" }}>
        Klicke auf ein oranges oder rotes Fach, um sofort 20 Fragen (inkl. Bildfragen) mit Gemini zu erstellen.
        {(busy || bulkRunning) && " Die KI arbeitet… (ca. 30–60 Sek.)"}
      </p>

      {/* Mehrfachauswahl Toolbar */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <button
          onClick={selectAllLow}
          className="adm-btn adm-btn-sm"
          style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
        >
          <CheckSquare className="h-3.5 w-3.5" /> Alle unvollständigen wählen ({missing})
        </button>
        {selected.size > 0 && (
          <>
            <button
              onClick={() => setSelected(new Set())}
              className="adm-btn adm-btn-sm"
              style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
            >
              <Square className="h-3.5 w-3.5" /> Auswahl aufheben
            </button>
            <button
              onClick={fillAll}
              disabled={bulkRunning}
              className="adm-btn adm-btn-primary adm-btn-sm"
              style={{ display: "flex", alignItems: "center", gap: "0.3rem", background: "#f97316", border: "none", color: "white", fontWeight: "bold" }}
            >
              {bulkRunning
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Erstelle {selected.size} Fächer…</>
                : <><Zap className="h-3.5 w-3.5" /> {selected.size} Fächer mit KI füllen</>
              }
            </button>
          </>
        )}
      </div>

      <div className="adm-coverage">
        {rows.map(({ p, subs }) => (
          <div key={p.id} className="adm-coverage-prof" data-testid={`admin-coverage-${p.id}`}>
            <div className="adm-row-title">
              {p.emoji} {p.name} <span className="adm-muted">· {subs.reduce((a, x) => a + x.n, 0)} Fragen</span>
            </div>
            <div className="adm-coverage-subs">
              {subs.map(({ s, n }) => {
                const low = n < MIN;
                const key = `${p.id}||${s.id}`;
                const chipKey = `${p.id}-${s.id}`;
                const isSelected = selected.has(key);
                const isBusy = busy === key || busy === chipKey;
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={(!low && !isSelected) || (!!busy && !isBusy)}
                    onClick={() => {
                      if (low) toggleSelect(key);
                      else if (!busy) fill(p, s);
                    }}
                    className={`adm-coverage-chip ${n === 0 ? "is-empty" : low ? "is-low" : ""} ${isSelected ? "is-selected" : ""}`}
                    style={isSelected ? { outline: "2px solid #f97316", outlineOffset: "1px" } : {}}
                    data-testid={`admin-coverage-${chipKey}`}
                    title={low ? `Klicken zum Auswählen / Einzeln erstellen` : `${s.name}: ${n} Fragen`}
                  >
                    {isBusy
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : isSelected
                        ? <CheckSquare className="h-3 w-3" />
                        : low
                          ? <Wand2 className="h-3 w-3" />
                          : null
                    }
                    {s.name} <strong>{n}</strong>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
